import { useMemo, useState, type FormEvent, type ReactNode } from "react";
import { ArrowLeft, FileCheck2, LoaderCircle, ShieldCheck, X } from "lucide-react";
import type { ConsultationInput, CrmPatientDetail } from "./api";

type Signer = NonNullable<CrmPatientDetail["capabilities"]["signer"]>;

interface Props {
  patientName: string;
  signer: Signer;
  onCancel: () => void;
  onSigned: (input: ConsultationInput, idempotencyKey: string) => Promise<void>;
}

function localDateTimeValue(): string {
  const now = new Date();
  const offset = now.getTimezoneOffset() * 60_000;
  return new Date(now.getTime() - offset).toISOString().slice(0, 16);
}

function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

export function ClinicalActComposer({ patientName, signer, onCancel, onSigned }: Props): ReactNode {
  const [phase, setPhase] = useState<"EDIT" | "REVIEW">("EDIT");
  const [occurredAt, setOccurredAt] = useState(localDateTimeValue);
  const [reason, setReason] = useState("");
  const [findings, setFindings] = useState("");
  const [anamnesis, setAnamnesis] = useState("");
  const [physicalExam, setPhysicalExam] = useState("");
  const [assessment, setAssessment] = useState("");
  const [diagnosis, setDiagnosis] = useState("");
  const [treatment, setTreatment] = useState("");
  const [indications, setIndications] = useState("");
  const [followUpAt, setFollowUpAt] = useState("");
  const [problemName, setProblemName] = useState("");
  const [problemStatus, setProblemStatus] = useState<
    "ACTIVE" | "CHRONIC" | "UNDER_FOLLOW_UP"
  >("ACTIVE");
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idempotencyKey = useMemo(() => crypto.randomUUID(), []);
  const occurredAtDate = new Date(occurredAt);
  const findingsValue = optional(findings);
  const anamnesisValue = optional(anamnesis);
  const physicalExamValue = optional(physicalExam);
  const assessmentValue = optional(assessment);
  const diagnosisValue = optional(diagnosis);
  const treatmentValue = optional(treatment);
  const indicationsValue = optional(indications);

  const input: ConsultationInput = {
    occurredAt: Number.isNaN(occurredAtDate.getTime())
      ? new Date().toISOString()
      : occurredAtDate.toISOString(),
    reason: reason.trim(),
    ...(findingsValue ? { findings: findingsValue } : {}),
    ...(anamnesisValue ? { anamnesis: anamnesisValue } : {}),
    ...(physicalExamValue ? { physicalExam: physicalExamValue } : {}),
    ...(assessmentValue ? { assessment: assessmentValue } : {}),
    ...(diagnosisValue ? { diagnosis: diagnosisValue } : {}),
    ...(treatmentValue ? { treatment: treatmentValue } : {}),
    ...(indicationsValue ? { indications: indicationsValue } : {}),
    ...(followUpAt ? { followUpAt: new Date(followUpAt).toISOString() } : {}),
    ...(problemName.trim()
      ? { problems: [{ name: problemName.trim(), status: problemStatus }] }
      : {}),
  };

  function review(event: FormEvent): void {
    event.preventDefault();
    setError(null);
    if (reason.trim().length < 2) {
      setError("Indicá el motivo de la atención antes de revisar.");
      return;
    }
    setPhase("REVIEW");
  }

  async function sign(): Promise<void> {
    setSubmitting(true);
    setError(null);
    try {
      await onSigned(input, idempotencyKey);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No pudimos firmar la atención.");
      setSubmitting(false);
    }
  }

  return (
    <section className="panel clinical-act" aria-labelledby="clinical-act-title">
      <div className="clinical-act-heading">
        <div>
          <span className="eyebrow">
            {phase === "EDIT" ? "Borrador clínico" : "Control previo a firma"}
          </span>
          <h2 id="clinical-act-title">
            {phase === "EDIT" ? `Nueva atención para ${patientName}` : "Revisá el acto clínico"}
          </h2>
        </div>
        <button
          className="icon-button"
          type="button"
          onClick={onCancel}
          aria-label="Cerrar nueva atención"
          disabled={submitting}
        >
          <X size={18} />
        </button>
      </div>

      {phase === "EDIT" ? (
        <form className="form-grid clinical-act-form" onSubmit={review}>
          <label>
            <span>Fecha y hora</span>
            <input
              type="datetime-local"
              value={occurredAt}
              max={localDateTimeValue()}
              onChange={(event) => setOccurredAt(event.target.value)}
              required
            />
          </label>
          <label>
            <span>Motivo de consulta</span>
            <input
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              minLength={2}
              maxLength={300}
              autoFocus
              required
            />
          </label>
          <label className="form-span-2">
            <span>Anamnesis</span>
            <textarea
              value={anamnesis}
              onChange={(event) => setAnamnesis(event.target.value)}
              maxLength={4000}
              rows={3}
              placeholder="Antecedentes, evolución y relato del tutor"
            />
          </label>
          <label className="form-span-2">
            <span>Examen físico</span>
            <textarea
              value={physicalExam}
              onChange={(event) => setPhysicalExam(event.target.value)}
              maxLength={4000}
              rows={3}
              placeholder="Signos vitales y examen por sistemas"
            />
          </label>
          <label className="form-span-2">
            <span>Hallazgos</span>
            <textarea
              value={findings}
              onChange={(event) => setFindings(event.target.value)}
              maxLength={2000}
              rows={4}
              placeholder="Examen, signos y observaciones relevantes"
            />
          </label>
          <label>
            <span>Evaluación clínica</span>
            <textarea
              value={assessment}
              onChange={(event) => setAssessment(event.target.value)}
              maxLength={2000}
              rows={3}
            />
          </label>
          <label>
            <span>Diagnóstico</span>
            <textarea
              value={diagnosis}
              onChange={(event) => setDiagnosis(event.target.value)}
              maxLength={1000}
              rows={3}
            />
          </label>
          <label>
            <span>Tratamiento e indicaciones</span>
            <textarea
              value={treatment}
              onChange={(event) => setTreatment(event.target.value)}
              maxLength={2000}
              rows={3}
            />
          </label>
          <label className="form-span-2">
            <span>Indicaciones para el tutor</span>
            <textarea
              value={indications}
              onChange={(event) => setIndications(event.target.value)}
              maxLength={4000}
              rows={3}
              placeholder="Cuidados, signos de alarma y pautas en lenguaje claro"
            />
          </label>
          <label>
            <span>Próximo control</span>
            <input
              type="datetime-local"
              value={followUpAt}
              min={localDateTimeValue()}
              onChange={(event) => setFollowUpAt(event.target.value)}
            />
          </label>
          <label>
            <span>Problema o condición relevante</span>
            <input
              value={problemName}
              onChange={(event) => setProblemName(event.target.value)}
              maxLength={200}
              placeholder="Ej: Enfermedad renal"
            />
            {problemName.trim() && (
              <select
                value={problemStatus}
                onChange={(event) =>
                  setProblemStatus(event.target.value as typeof problemStatus)
                }
              >
                <option value="ACTIVE">Activo</option>
                <option value="CHRONIC">Crónico</option>
                <option value="UNDER_FOLLOW_UP">En seguimiento</option>
              </select>
            )}
          </label>
          {error && (
            <p className="form-error form-span-2" role="alert">
              {error}
            </p>
          )}
          <div className="form-actions form-span-2">
            <button className="button button-secondary" type="button" onClick={onCancel}>
              Cancelar
            </button>
            <button className="button button-primary" type="submit">
              Revisar antes de firmar
            </button>
          </div>
        </form>
      ) : (
        <div className="clinical-review">
          <div className="signature-proof">
            <ShieldCheck size={22} aria-hidden="true" />
            <div>
              <span>Firmante verificado</span>
              <strong>{signer.fullName}</strong>
              <small>
                Mat. {signer.licenseNumber} · {signer.jurisdiction}
              </small>
            </div>
          </div>
          <dl className="clinical-review-grid">
            <div>
              <dt>Paciente</dt>
              <dd>{patientName}</dd>
            </div>
            <div>
              <dt>Fecha de atención</dt>
              <dd>{new Date(input.occurredAt ?? "").toLocaleString("es-AR")}</dd>
            </div>
            <div className="review-span-2">
              <dt>Motivo</dt>
              <dd>{input.reason}</dd>
            </div>
            <ReviewField label="Hallazgos" value={input.findings} />
            <ReviewField label="Anamnesis" value={input.anamnesis} />
            <ReviewField label="Examen físico" value={input.physicalExam} />
            <ReviewField label="Evaluación" value={input.assessment} />
            <ReviewField label="Diagnóstico" value={input.diagnosis} />
            <ReviewField label="Tratamiento e indicaciones" value={input.treatment} wide />
            <ReviewField label="Indicaciones" value={input.indications} wide />
            <ReviewField
              label="Próximo control"
              value={
                input.followUpAt
                  ? new Date(input.followUpAt).toLocaleString("es-AR")
                  : undefined
              }
            />
            <ReviewField label="Problema registrado" value={input.problems?.[0]?.name} />
          </dl>
          <label className="signature-acknowledgement">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(event) => setAcknowledged(event.target.checked)}
            />
            <span>
              Confirmo que revisé el contenido. Al firmar será inmutable; las correcciones se
              registrarán mediante adendas o anulación trazable.
            </span>
          </label>
          {error && (
            <p className="form-error" role="alert">
              {error}
            </p>
          )}
          <div className="form-actions">
            <button
              className="button button-secondary"
              type="button"
              onClick={() => setPhase("EDIT")}
              disabled={submitting}
            >
              <ArrowLeft size={16} /> Volver a editar
            </button>
            <button
              className="button button-primary button-sign"
              type="button"
              onClick={() => void sign()}
              disabled={!acknowledged || submitting}
            >
              {submitting ? (
                <LoaderCircle className="spin" size={17} aria-hidden="true" />
              ) : (
                <FileCheck2 size={17} aria-hidden="true" />
              )}
              {submitting ? "Firmando…" : "Firmar atención"}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function ReviewField({
  label,
  value,
  wide = false,
}: {
  label: string;
  value: string | undefined;
  wide?: boolean;
}): ReactNode {
  return (
    <div className={wide ? "review-span-2" : undefined}>
      <dt>{label}</dt>
      <dd>{value ?? "Sin información consignada"}</dd>
    </div>
  );
}
